import { DateTime } from 'luxon';
import { BaseModuleBody, LocalizedField, ModuleChange, ModuleChangeChangeTypeEnum } from '../../../API';
import { UiModuleBody, UIModuleComparison } from '../../../state/Layouts';

const alternativeTitleFields = ['textTitle'];

// Fields that should be ignored during comparison as they are dynamic/system generated
const IGNORED_FIELDS = new Set(['moduleId', 'hash']);

export function changeMovedAndModified(change: ModuleChange): boolean {
  return (
    change.changeType === ModuleChangeChangeTypeEnum.MODIFIED &&
    change.oldPosition !== undefined &&
    change.newPosition !== undefined &&
    change.oldPosition !== change.newPosition
  );
}

/**
 * Compares two arrays for equality, with order-independent comparison for primitive arrays
 */
export function compareArrays(arr1: unknown[], arr2: unknown[]): boolean {
  if (arr1.length !== arr2.length) return false;

  // For primitive arrays, sort and compare
  if (arr1.every((item) => typeof item !== 'object') && arr2.every((item) => typeof item !== 'object')) {
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return JSON.stringify(sorted1) === JSON.stringify(sorted2);
  }

  // For complex arrays, use JSON comparison (maintains order)
  return JSON.stringify(arr1) === JSON.stringify(arr2);
}

/**
 * Compares two objects for equality, with order-independent comparison
 */
export function compareObjects(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === null || obj2 === null) return obj1 === obj2;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
  if (obj1 === undefined || obj2 === undefined) return obj1 === obj2;

  const object1 = obj1 as Record<string, unknown>;
  const object2 = obj2 as Record<string, unknown>;

  const keys1 = Object.keys(object1).sort();
  const keys2 = Object.keys(object2).sort();

  if (keys1.length !== keys2.length) return false;
  if (JSON.stringify(keys1) !== JSON.stringify(keys2)) return false;

  // Compare values for each key
  return keys1.every((key) => {
    const val1 = object1[key];
    const val2 = object2[key];

    if (Array.isArray(val1) && Array.isArray(val2)) {
      return compareArrays(val1, val2);
    }
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      return compareObjects(val1, val2);
    }
    return val1 === val2;
  });
}

/**
 * Detects if two UI modules have been modified by comparing their properties
 */
export function detectModuleModification(
  source: UIModuleComparison | null,
  target: UIModuleComparison | null,
  position: number
): ModuleChange | null {
  if (!source || !target) return null;
  if (source.moduleType !== target.moduleType) return null;

  const fieldChanges: Array<{ fieldName: string; oldValue: unknown; newValue: unknown }> = [];

  // Get all unique keys from both objects
  const allKeys = new Set([...Object.keys(source), ...Object.keys(target)]);

  // Check each field for changes
  allKeys.forEach((fieldName) => {
    // Skip ignored fields
    if (IGNORED_FIELDS.has(fieldName)) return;

    const sourceValue = (source as unknown as Record<string, unknown>)[fieldName];
    const targetValue = (target as unknown as Record<string, unknown>)[fieldName];

    // Skip if both are undefined/null
    if ((sourceValue === null || sourceValue === undefined) && (targetValue === null || targetValue === undefined))
      return;

    // Compare values based on type
    let isEqual = false;

    if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
      // Array comparison
      isEqual = compareArrays(sourceValue, targetValue);
    } else if (typeof sourceValue === 'object' && typeof targetValue === 'object') {
      // Object comparison
      isEqual = compareObjects(sourceValue, targetValue);
    } else {
      // Primitive comparison
      const sourceStr = sourceValue !== null && sourceValue !== undefined ? String(sourceValue) : '';
      const targetStr = targetValue !== null && targetValue !== undefined ? String(targetValue) : '';
      isEqual = sourceStr === targetStr;
    }

    if (!isEqual) {
      fieldChanges.push({
        fieldName,
        oldValue: sourceValue,
        newValue: targetValue
      });
    }
  });

  // If any changes detected, return modification
  if (fieldChanges.length > 0) {
    const moduleTitle = getDisplayTitle(target.title, target.moduleType || 'Module', target);

    return {
      changeType: 'MODIFIED' as const,
      title: moduleTitle,
      moduleType: target.moduleType || 'Module',
      oldPosition: position,
      newPosition: position,
      fieldChanges
    } as ModuleChange;
  }

  return null;
}

export function getBusinessFriendlyFieldName(fieldName: string): string {
  // Convert camelCase or PascalCase to business-friendly format (e.g., textTile -> Text Tile)
  if (!fieldName) return '';
  // Insert a space before all caps and capitalize the first letter
  const spaced = fieldName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // textTile -> text Tile
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2'); // HTMLParser -> HTML Parser
  // Capitalize the first letter
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function getBusinessFriendlyFieldValue(value: string | null | undefined | object): string {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'string') {
    if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
      return DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED);
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => getBusinessFriendlyFieldValue(item)).join(', ');
  }
  if (typeof value === 'object') {
    return Object.values(value)
      .map((item) => getBusinessFriendlyFieldValue(item))
      .join(', ');
  }
  return value;
}

const isValidTitle = (title: LocalizedField | string | undefined) => {
  if (typeof title === 'string' && title.trim() !== '') return true;
  if (typeof title === 'object') {
    return Object.values(title).some((value) => value.trim() !== '');
  }
  return false;
};

const getTitleValue = (title: LocalizedField | string | undefined) => {
  if (typeof title === 'string' && title.trim() !== '') return title;
  if (typeof title === 'object') {
    return Object.values(title).find((value) => value.trim() !== '');
  }
  return undefined;
};

export function getDisplayTitle(
  title: LocalizedField | string | undefined,
  moduleType: string,
  uiModule?: BaseModuleBody | null
): string | undefined {
  if (alternativeTitleFields?.length && uiModule) {
    const customField = alternativeTitleFields.find(
      (field) =>
        uiModule[field as keyof UiModuleBody] &&
        isValidTitle(uiModule[field as keyof UiModuleBody] as LocalizedField | string | undefined)
    );
    if (customField) {
      return getTitleValue(uiModule[customField as keyof UiModuleBody] as LocalizedField | string | undefined);
    }
  }
  if (isValidTitle(title)) return getTitleValue(title);
  return moduleType;
}
